import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddTeammateForm from 'Shared/components/Forms/AddTeammateForm/AddTeammateForm';
import { defaultTeammateMessage } from 'Shared/helpers';
import { useAddTeammate } from 'Shared/utils/hooks/use-add-teammate';
import {
  setOptionsForInviteToTeammateModal,
  resetOptionsForInviteToTeammateModal,
} from 'Shared/actions/optionsForInviteToTeammateModal';
import { useQueryParam } from 'Shared/utils/hooks';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';

import AddTeammateLogo from './AddTeammateLogo/AddTeammateLogo';

function InviteToTeamModal() {
  const dispatch = useDispatch();
  const [addUserParam, setAddUserParam] = useQueryParam(QueryParamConstants.ADD_USER_MODAL);

  const { isOpen, mentionedNewEmail, isDirectInvite, isEmailMandatory, modalHeaderTitle, isRedirectTo } = useSelector(
    state => state.optionsForInviteToTeammateModal
  );

  const isModalOpen = isOpen || addUserParam;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formValues, onTextFieldsChange, disabledSubmit, onSubmit, setFormValues } = useAddTeammate({
    initialEmail: mentionedNewEmail || '',
    isSubmitting,
    setIsSubmitting,
  });

  useEffect(() => {
    if (addUserParam) {
      dispatch(
        setOptionsForInviteToTeammateModal({
          isEmailMandatory: true,
          modalHeaderTitle: 'Add User',
          isRedirectTo: false,
        })
      );
    }
  }, [addUserParam]);

  useEffect(() => setFormValues({ ...formValues, email: mentionedNewEmail || '' }), [mentionedNewEmail]);

  const handleModalClose = useCallback(() => {
    if (addUserParam) setAddUserParam(null);
    dispatch(resetOptionsForInviteToTeammateModal());
  }, [dispatch, addUserParam]);

  const handleSubmitClick = async () => {
    await onSubmit({ isRedirectTo });
    setFormValues({ firstName: '', lastName: '', email: '', personalMessage: defaultTeammateMessage });
    handleModalClose();
  };

  const headerText = () => {
    if (modalHeaderTitle) return modalHeaderTitle;
    return isDirectInvite ? `Invite ${mentionedNewEmail} to PI Perform` : 'Add teammate';
  };

  return (
    <WpModal size="sm" isOpen={isModalOpen} onToggle={handleModalClose}>
      <WpModalHeader>{headerText()}</WpModalHeader>
      <WpModalBody>
        <div className="mb-3 text-center">
          <AddTeammateLogo width="80" />
        </div>
        <p>
          {isDirectInvite
            ? `Inviting ${mentionedNewEmail} will create a private space between the two of you to collaborate on discussion topics and action items.`
            : 'Add a teammate to PI Perform so you can start collaborating with them. Adding them will create a private space between the two of you to collaborate on discussion topics and action items.'}
        </p>
        <AddTeammateForm
          isDirectInvite={isDirectInvite}
          isEmailMandatory={isEmailMandatory}
          formValues={formValues}
          onTextFieldsChange={onTextFieldsChange}
        />
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton disabled={isSubmitting} variant="tertiary" onClick={handleModalClose}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSubmitClick} disabled={disabledSubmit} loading={isSubmitting}>
            Add
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default InviteToTeamModal;
